import { OutputPresetId } from '@/content/instagram'
import { FilmFormatId } from '@/content/instantFilm'
import { AdjustmentChannel } from '@/core/document'
import {
  CollageColorPresetId,
  CollageFamily,
  CollageTemplateId,
  GradientPresetId,
} from '@/content/collageTemplates'
import { ExportFormat, ResizeMode } from '@/core/export/types'
import { PaperPresetId, PhotoPlacement } from '@/tools/frame/params'
import { ToolTab } from '@/tools/types'
import { PREVIEW_OLDER_POST_OFFSETS, PreviewTab, ThemeMode } from '@/core/store/uiState'

const STATIC_TEST_IDS = {
  bottomSheet: 'bottom-sheet',
  bottomSheetHandle: 'bottom-sheet-handle',
  activePhotoBadge: 'active-photo-badge',
  activePhotoContext: 'active-photo-context',
  activePhotoMarker: 'active-photo-marker',
  activePhotoNext: 'active-photo-next',
  activePhotoPrevious: 'active-photo-previous',
  activePhotoWindow: 'active-photo-window',
  appVersion: 'app-version',
  adjustmentsReset: 'adjustments-reset',
  collageEnter: 'collage-enter',
  collageCellRemove: 'collage-cell-remove',
  collageCellReplace: 'collage-cell-replace',
  collageBackgroundBlur: 'collage-background-blur',
  collageBackgroundColor: 'collage-background-color',
  collageBackgroundImage: 'collage-background-image',
  collageGap: 'collage-gap',
  collageLeave: 'collage-leave',
  collageOtherRatios: 'collage-other-ratios',
  collagePrintBorder: 'collage-print-border',
  collageTray: 'collage-tray',
  customLongestSide: 'custom-longest-side',
  compositionEditor: 'composition-editor',
  canvasFit: 'canvas-fit',
  contactEmail: 'contact-email',
  contactInstagram: 'contact-instagram',
  cropZoom: 'crop-zoom',
  cropFineTune: 'crop-fine-tune',
  dropZone: 'drop-zone',
  diagnosticsCopy: 'diagnostics-copy',
  diagnosticsDialog: 'diagnostics-dialog',
  errorScreen: 'error-screen',
  errorTryAgain: 'error-try-again',
  exportDownloadAll: 'export-download-all',
  exportDownloadZip: 'export-download-zip',
  exportEstimate: 'export-estimate',
  exportSave: 'export-save',
  exportShare: 'export-share',
  exportShareHint: 'export-share-hint',
  exportWarnings: 'export-warnings',
  flipHorizontal: 'flip-horizontal',
  flipVertical: 'flip-vertical',
  frameAdd: 'frame-add',
  frameCaption: 'frame-caption',
  frameDate: 'frame-date',
  frameDateFormat: 'frame-date-format',
  framePaperColor: 'frame-paper-color',
  framePrintSize: 'frame-print-size',
  framePrintShadow: 'frame-print-shadow',
  frameRecenter: 'frame-recenter',
  frameRemove: 'frame-remove',
  frameStampDate: 'frame-stamp-date',
  frameWindowShadow: 'frame-window-shadow',
  gridOverlay: 'grid-overlay',
  gridToggle: 'grid-toggle',
  frameWrittenDate: 'frame-written-date',
  outputDimensions: 'output-dimensions',
  previewCanvas: 'preview-canvas',
  previewOpen: 'preview-open',
  previewOverlay: 'preview-overlay',
  previewLoading: 'preview-loading',
  previewProfile: 'preview-profile',
  previewCarouselMarker: 'preview-carousel-marker',
  previewPost: 'preview-post',
  previewPostMedia: 'preview-post-media',
  previewCarouselBadge: 'preview-carousel-badge',
  previewStory: 'preview-story',
  previewStoryTopChrome: 'preview-story-top-chrome',
  previewStoryBottomChrome: 'preview-story-bottom-chrome',
  privacyLink: 'privacy-link',
  jpegQualitySlider: 'jpeg-quality-slider',
  qualityCompare: 'quality-compare',
  qualityOverlay: 'quality-overlay',
  qualityLoading: 'quality-loading',
  qualityNavigator: 'quality-navigator',
  qualityLoupeOriginal: 'quality-loupe-original',
  qualityLoupeJpeg: 'quality-loupe-jpeg',
  qualityDifference: 'quality-difference',
  qualityDiffToggle: 'quality-diff-toggle',
  qualityWindowBytes: 'quality-window-bytes',
  qualityWholeFile: 'quality-whole-file',
  qualitySwipeDivider: 'quality-swipe-divider',
  openRecent: 'open-recent',
  persistenceStatus: 'persistence-status',
  pinnedExport: 'pinned-export',
  plainFrameBorder: 'plain-frame-border',
  plainFrameColor: 'plain-frame-color',
  orientationReset: 'orientation-reset',
  recentEdits: 'recent-edits',
  recentEditsClear: 'recent-edits-clear',
  resizeToggle: 'resize-toggle',
  rotateLeft: 'rotate-left',
  rotateRight: 'rotate-right',
  straightenSlider: 'straighten-slider',
  splitMode: 'split-mode',
  splitRows: 'split-rows',
  splitTileRatio: 'split-tile-ratio',
  splitTiles: 'split-tiles',
  safeZonesOverlay: 'safe-zones-overlay',
  safeZonesToggle: 'safe-zones-toggle',
  settingsOpen: 'settings-open',
  settingsPanel: 'settings-panel',
  topBarActions: 'top-bar-actions',
  toolStrip: 'tool-strip',
  whatsNewDialog: 'whats-new-dialog',
  whatsNewGotIt: 'whats-new-got-it',
  whatsNewMore: 'whats-new-more',
  whatsNewNotice: 'whats-new-notice',
  whatsNewOpen: 'whats-new-open',
  undo: 'undo',
  redo: 'redo',
} as const

export const TEST_IDS = {
  ...STATIC_TEST_IDS,
  adjustmentSlider: (channel: AdjustmentChannel) => `adjustment-slider-${channel}`,
  exportFormat: (format: ExportFormat) => `export-format-${format}`,
  collageBackgroundPreset: (id: CollageColorPresetId) => `collage-background-preset-${id}`,
  collageFamily: (family: CollageFamily) => `collage-family-${family}`,
  collageCell: (index: number) => `collage-cell-${index}`,
  collageGradient: (id: GradientPresetId) => `collage-gradient-${id}`,
  collageGradientColor: (index: number) => `collage-gradient-color-${index}`,
  collageTemplate: (id: CollageTemplateId) => `collage-template-${id}`,
  collageTrayNode: (index: number) => `collage-tray-node-${index}`,
  frameFormat: (id: FilmFormatId) => `frame-format-${id}`,
  framePaperPreset: (id: PaperPresetId) => `frame-paper-${id}`,
  framePlacement: (placement: PhotoPlacement) => `frame-placement-${placement}`,
  preset: (id: OutputPresetId) => `preset-${id}`,
  presetInfo: (id: OutputPresetId) => `preset-info-${id}`,
  resizeMode: (mode: ResizeMode) => `resize-mode-${mode}`,
  toolTab: (tab: ToolTab) => `tool-tab-${tab}`,
  previewTab: (tab: PreviewTab) => `preview-tab-${tab}`,
  previewOlderOffset: (offset: number) => `preview-older-offset-${offset}`,
  previewProfileTile: (uploadNumber: number) => `preview-profile-tile-${uploadNumber}`,
  previewPostPicker: (uploadNumber: number) => `preview-post-picker-${uploadNumber}`,
  recentEditCard: (id: string) => `recent-edit-${id}`,
  recentEditDelete: (id: string) => `recent-edit-delete-${id}`,
  settingsThemeMode: (mode: ThemeMode) => `settings-theme-${mode}`,
} as const

export function configuredTestIds(): readonly string[] {
  return [
    ...Object.values(STATIC_TEST_IDS),
    ...Object.values(AdjustmentChannel).map((channel) => TEST_IDS.adjustmentSlider(channel)),
    ...Object.values(ExportFormat).map((format) => TEST_IDS.exportFormat(format)),
    ...Object.values(CollageColorPresetId).map((id) => TEST_IDS.collageBackgroundPreset(id)),
    ...Object.values(CollageFamily).map((family) => TEST_IDS.collageFamily(family)),
    ...Object.values(CollageTemplateId).map((id) => TEST_IDS.collageTemplate(id)),
    ...Object.values(GradientPresetId).map((id) => TEST_IDS.collageGradient(id)),
    ...Object.values(FilmFormatId).map((id) => TEST_IDS.frameFormat(id)),
    ...Object.values(OutputPresetId).flatMap((id) => [TEST_IDS.preset(id), TEST_IDS.presetInfo(id)]),
    ...Object.values(PaperPresetId).map((id) => TEST_IDS.framePaperPreset(id)),
    ...Object.values(PhotoPlacement).map((placement) => TEST_IDS.framePlacement(placement)),
    ...Object.values(ResizeMode).map((mode) => TEST_IDS.resizeMode(mode)),
    ...Object.values(ToolTab).map((tab) => TEST_IDS.toolTab(tab)),
    ...Object.values(PreviewTab).map((tab) => TEST_IDS.previewTab(tab)),
    ...Object.values(ThemeMode).map((mode) => TEST_IDS.settingsThemeMode(mode)),
    ...PREVIEW_OLDER_POST_OFFSETS.map((offset) => TEST_IDS.previewOlderOffset(offset)),
  ]
}

export function validateTestIds(ids: readonly string[] = configuredTestIds()): void {
  const seen = new Set<string>()
  for (const id of ids) {
    if (!id.trim()) throw new Error('Test id configuration contains an empty id')
    if (seen.has(id)) throw new Error(`Test id configuration contains duplicate id "${id}"`)
    seen.add(id)
  }
}
